import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import * as JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import { CallSendService } from './call-send.service';
import { CallLogRecord, CallProgressStatus, CrmSource, WebRtcConfig } from '../models/call-log.model';

export type CallPhase = 'idle' | 'unavailable' | 'connecting' | 'ringing' | 'in_call' | 'ended';

/** How a finished call ended - drives the console's default outcome. */
export type CallResult = 'completed' | 'no_answer' | 'busy' | 'failed';

export interface StartCallRequest {
  crmSource: CrmSource;
  subjectUuid: string;
  number: string;
  contactName: string | null;
  contactSubtitle: string | null;
}

export interface ActiveCall {
  phase: CallPhase;
  request: StartCallRequest;
  callLogId: number | null;
  /** Our registered number the call goes out as. */
  callerId: string;
  answeredAt: number | null;
  endedAt: number | null;
  muted: boolean;
  onHold: boolean;
  result: CallResult | null;
  /** Shown in the console when the call couldn't be placed / failed. */
  message: string | null;
}

/**
 * In-browser calling for the admin Call console - one SIP line (registered
 * to the PBX behind our company number) shared by the whole app, so a call
 * keeps going while the agent moves between CRM records/pages.
 *
 * Owns the JsSIP user agent and the live session; CallConsoleComponent is
 * just its view. Every call is recorded in call_logs up front (dial, via
 * 'webrtc') and its status reported as it moves along (progress), so the
 * record's Call history stays correct even if the agent never wraps up.
 */
@Injectable({
  providedIn: 'root'
})
export class WebRtcCallService {
  private readonly stateSubject = new BehaviorSubject<ActiveCall | null>(null);
  readonly state$ = this.stateSubject.asObservable();

  /** Emits whenever a call's log row changes, so open Call tabs refresh. */
  readonly callLogged$ = new Subject<CallLogRecord>();

  private ua: JsSIP.UA | null = null;
  private config: WebRtcConfig | null = null;
  private session: RTCSession | null = null;
  private remoteAudio: HTMLAudioElement | null = null;

  constructor(
    private callSendService: CallSendService,
    private zone: NgZone
  ) {}

  get current (): ActiveCall | null {
    return this.stateSubject.value;
  }

  get isBusy (): boolean {
    const phase = this.current?.phase;
    return phase === 'connecting' || phase === 'ringing' || phase === 'in_call';
  }

  /** Opens the console without dialling - used when in-browser calling
   *  isn't configured, so the agent sees why and can use the softphone. */
  showUnavailable (request: StartCallRequest, callerId: string, message: string): void {
    if (this.isBusy) return;
    this.stateSubject.next({ ...this.blankCall(request, callerId), phase: 'unavailable', message });
  }

  async start (request: StartCallRequest): Promise<void> {
    if (this.isBusy) return;

    this.stateSubject.next(this.blankCall(request, this.config?.caller_id ?? ''));

    try {
      const config = await this.ensureConfig();
      this.patch({ callerId: config.caller_id });

      const log = await firstValueFrom(this.callSendService.dial(request.crmSource, request.subjectUuid, request.number, 'webrtc'));
      this.patch({ callLogId: log.data.id });
      this.callLogged$.next(log.data);

      const ua = await this.ensureUserAgent(config);
      this.placeSession(ua, config, request.number);
    } catch (err: any) {
      const message = err?.error?.message ?? err?.message ?? 'Could not start the call.';
      this.finish('failed', message);
    }
  }

  hangUp (): void {
    if (this.session && !this.session.isEnded()) {
      this.session.terminate();
    } else if (this.current && this.current.phase !== 'ended') {
      this.finish('failed', null);
    }
  }

  toggleMute (): void {
    if (!this.session || this.current?.phase !== 'in_call') return;
    if (this.current.muted) {
      this.session.unmute({ audio: true });
    } else {
      this.session.mute({ audio: true });
    }
    this.patch({ muted: !this.current.muted });
  }

  toggleHold (): void {
    if (!this.session || this.current?.phase !== 'in_call') return;
    if (this.current.onHold) {
      this.session.unhold();
    } else {
      this.session.hold();
    }
    this.patch({ onHold: !this.current.onHold });
  }

  sendDigit (digit: string): void {
    if (this.session && this.current?.phase === 'in_call') {
      this.session.sendDTMF(digit);
    }
  }

  /** Closes the console once the agent is done with the ended call. */
  dismiss (): void {
    if (this.isBusy) return;
    this.stateSubject.next(null);
  }

  private placeSession (ua: JsSIP.UA, config: WebRtcConfig, number: string): void {
    const target = `sip:${this.dialable(number)}@${config.domain}`;
    const callerUri = `sip:${this.dialable(config.caller_id)}@${config.domain}`;

    const session = ua.call(target, {
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers: config.stun_servers.map((urls) => ({ urls })) },
      // Ask the PBX to present our registered number - the extension's
      // own outbound caller id should be set to the same number there.
      extraHeaders: [`P-Asserted-Identity: <${callerUri}>`],
      fromDisplayName: config.display_name
    });
    this.session = session;

    session.connection?.addEventListener('track', (event: RTCTrackEvent) => this.playRemote(event.streams[0]));

    session.on('progress', () => this.zone.run(() => {
      if (this.current?.phase === 'connecting') {
        this.patch({ phase: 'ringing' });
        this.report('ringing');
      }
    }));

    session.on('confirmed', () => this.zone.run(() => {
      this.patch({ phase: 'in_call', answeredAt: Date.now() });
      this.report('in_progress');
    }));

    session.on('ended', () => this.zone.run(() => this.finish('completed', null)));

    session.on('failed', (e: any) => this.zone.run(() => {
      const cause: string = e?.cause ?? '';
      const result: CallResult = cause === JsSIP.C.causes.BUSY ? 'busy'
        : [JsSIP.C.causes.NO_ANSWER, JsSIP.C.causes.REJECTED, JsSIP.C.causes.CANCELED, JsSIP.C.causes.UNAVAILABLE].includes(cause as any) ? 'no_answer'
        : 'failed';
      const message = result === 'failed' ? `Call failed${cause ? ` (${cause})` : ''}.` : null;
      this.finish(result, message);
    }));
  }

  private finish (result: CallResult, message: string | null): void {
    const call = this.current;
    if (!call || call.phase === 'ended' || call.phase === 'unavailable') return;

    const endedAt = Date.now();
    const answered = call.answeredAt !== null;
    // A call that ended normally but was never answered is a no-answer.
    const finalResult: CallResult = result === 'completed' && !answered ? 'no_answer' : result;
    const duration = answered ? Math.round((endedAt - (call.answeredAt as number)) / 1000) : 0;

    this.session = null;
    this.stopRemote();
    this.patch({ phase: 'ended', endedAt, result: finalResult, message, muted: false, onHold: false });
    this.report(finalResult, duration);
  }

  private report (status: CallProgressStatus, durationSeconds: number | null = null): void {
    const id = this.current?.callLogId;
    if (!id) return;
    this.callSendService.progress(id, status, durationSeconds).subscribe({
      next: (res) => this.callLogged$.next(res.data)
    });
  }

  private async ensureConfig (): Promise<WebRtcConfig> {
    if (!this.config) {
      const res = await firstValueFrom(this.callSendService.webrtcConfig());
      this.config = res.data;
    }
    return this.config;
  }

  private ensureUserAgent (config: WebRtcConfig): Promise<JsSIP.UA> {
    if (this.ua?.isRegistered()) return Promise.resolve(this.ua);

    return new Promise((resolve, reject) => {
      this.ua?.stop();
      const ua = new JsSIP.UA({
        sockets: [new JsSIP.WebSocketInterface(config.ws_url)],
        uri: config.sip_uri,
        authorization_user: config.username,
        password: config.password,
        display_name: config.display_name,
        register: true,
        session_timers: false
      });

      ua.on('registered', () => resolve(ua));
      ua.on('registrationFailed', (e: any) => {
        reject(new Error(`Could not connect the phone line${e?.cause ? ` (${e.cause})` : ''}.`));
      });

      this.ua = ua;
      ua.start();
    });
  }

  private playRemote (stream: MediaStream | undefined): void {
    if (!stream) return;
    if (!this.remoteAudio) {
      this.remoteAudio = new Audio();
      this.remoteAudio.autoplay = true;
    }
    this.remoteAudio.srcObject = stream;
    this.remoteAudio.play().catch(() => undefined);
  }

  private stopRemote (): void {
    if (this.remoteAudio) {
      this.remoteAudio.srcObject = null;
    }
  }

  private dialable (number: string): string {
    return number.replace(/[^\d+]/g, '');
  }

  private blankCall (request: StartCallRequest, callerId: string): ActiveCall {
    return {
      phase: 'connecting',
      request,
      callLogId: null,
      callerId,
      answeredAt: null,
      endedAt: null,
      muted: false,
      onHold: false,
      result: null,
      message: null
    };
  }

  private patch (changes: Partial<ActiveCall>): void {
    const call = this.current;
    if (call) {
      this.stateSubject.next({ ...call, ...changes });
    }
  }
}
